import { ObjectType } from '@quromedical/fhir-common';
import { ConsumableCreateInputRequest } from '@quromedical/models';
import { CrudForm } from 'components/form';
import { Field, SelectOption } from 'components/types';
import { ConsumableFetcher } from 'integration';
import { useCallback, useState } from 'react';
import { strings } from 'strings';
import * as yup from 'yup';

interface FormProps {
  title?: string;
  consumableFetcher: ConsumableFetcher;
  onAdd: () => void;
  onChange: (data: Partial<ConsumableCreateInputRequest>, hasErrors: boolean) => void;
  onRemove: () => void;
  canAdd: boolean;
  canRemove: boolean;
  showErrors: boolean;
  initialValues: Partial<ConsumableCreateInputRequest>;
}

type FormData = {
  consumable: SelectOption<any, string>;
  quantity: number;
  dateOfUse: string;
};

export const schema = yup
  .object<ObjectType<FormData>>({
    consumable: yup.object().required(),
    quantity: yup.number().positive().required(),
    dateOfUse: yup.date().required(),
  })
  .required();

const createFields = (consumableFetcher: ConsumableFetcher): Field<FormData>[] => [
  {
    subfields: [
      {
        key: 'consumable',
        label: strings.LabelConsumable,
        initialSelection: [],
        searchable: true,
        type: 'combobox-single',
        fetcher: consumableFetcher,
        returnFullOption: true,
      },
      {
        key: 'quantity',
        label: strings.LabelConsumableQuantity,
        type: 'number-box',
      },
    ],
  },
  {
    subfields: [
      {
        key: 'dateOfUse',
        label: strings.LabelConsumableUsageDate,
        type: 'date-component',
        maskType: 'date',
      },
    ],
  },
];

const mapInitialValuesToFormData = (
  initialValues: Partial<ConsumableCreateInputRequest>
): Partial<FormData> => ({
  ...initialValues,
  dateOfUse: new Date().toISOString(),
});

export const ConsumableForm = ({
  title,
  consumableFetcher,
  onAdd,
  onChange,
  onRemove,
  canAdd,
  canRemove,
  showErrors,
  initialValues,
}: FormProps) => {
  const [consumableDisplay, setConsumableDisplay] = useState(title);

  const handleChange = useCallback(
    (data: Partial<FormData>) => {
      const consumable = data.consumable;

      // manually check schema since the internal form does not validate if not touched
      const isValid = schema.isValidSync(data);
      setConsumableDisplay(consumable?.display || title);
      onChange(
        {
          medpraxCode: consumable?.value,
          quantity: data.quantity,
          dateOfUse: data.dateOfUse,
        },
        !isValid
      );
    },
    [onChange, title]
  );

  const initialData = mapInitialValuesToFormData(initialValues);

  const onSubmit = useCallback(() => {
    onAdd();
    return {};
  }, [onAdd]);

  const fields = createFields(consumableFetcher);

  return (
    <CrudForm<FormData>
      title={consumableDisplay}
      validationSchema={schema}
      fields={fields}
      initialValues={initialData}
      hasCard={false}
      buttonText={strings.FormListAddItem}
      onSecondarySubmit={onRemove}
      secondaryButtonText={strings.FormListRemoveItem}
      showSecondarySubmitButton={canRemove}
      onChange={handleChange}
      onSubmit={onSubmit}
      validateOnChange
      validateOnMount={showErrors}
      onChangeDebounceTime={0}
      showErrors={showErrors}
      showSubmitButton={canAdd}
      buttonVariant="outlined"
    />
  );
};
